import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DatesProvider } from "../../../../../dates/DatesContext";
import moment from "moment";
import BreadcrumbDates, { type Breadcrumb } from "../../../../../components/core/blocks/BreadcrumbDates";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../../../utils/dashboardLinks";
import { useModuleEnabled } from "../../../../../redux/selectors/modules";
import checkTierAccess from "../../../../../utils/checkTierAccess";
import PaymentDiscovery from "../../../../../views/billing/PaymentDiscovery";
import EmptyState from "../../../../../components/custom/merchandise/EmptyState";
import { useDates } from "../../../../../dates/DatesContext";
import { useAppSelector } from "../../../../../redux/hooks";
import { PageContainer } from "../../../../../components/core/basic/PageContainer";
import { css } from "../../../../../../styled-system/css";
import { Accordion } from "../../../../../components/core/basic/Accordion";
import CategoryTable from "../../../../../modules/pricingv2/components/CategoryTable";
import DownloadReport from "../../../../../modules/perf_reports/DownloadReport";
import { useFetch } from "../../../../../hooks/api";
import { keepPreviousData } from "@tanstack/react-query";
import Loading from "../../../../../components/core/blocks/Loading";
import { useLocation, createFileRoute } from "@tanstack/react-router";
import { DevErrorDisplay } from "../../../../../components/core/blocks/DevErrorDisplay";
import { useSimpleCan } from "../../../../../config/casl/casl";

const Dashboard = () => {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const userCanExportPricingPlans = useSimpleCan("export", "PricingPlan");

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "category_id",
  };

  const breadCrumbItems: Breadcrumb[] = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.pricingv2["Pricing Dashboard"]),
      href: "/user/pricing/management",
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: t(($) => $.pricingv2["Dashboards"]),
    },
  ];

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const planQuery = useFetch<any>(["plan_report"], `/api/data_report/pricingplan_report`, filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
  });

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      }
      return "COLLECTING_DATA";
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace]);

  if (showEmpty) {
    return <EmptyState type={showEmpty} />;
  }

  if (planQuery.isLoading) {
    return <Loading fullPage />;
  }

  if (!planQuery.isSuccess) {
    return <DevErrorDisplay error={planQuery.error} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} />
      <PageContainer withBreadcrumb padding={"large"}>
        <h3 className={css({ margin: 0 })}>{t(($) => $.pricingv2["Dynamic pricing management"])}</h3>
        <Accordion title={t(($) => $.pricingv2["Categories"])} defaultExpanded={true}>
          <CategoryTable stats={planQuery.data?.category_id} />
          {userCanExportPricingPlans && (
            <DownloadReport url_path={"category_report"} filters={filters} report_dl={"category"} />
          )}
        </Accordion>
      </PageContainer>
    </>
  );
};

const PricingManagementOverview = () => {
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");

  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);

  const location = useLocation();

  const DYNAMIC_PRICING_SUBSCRIPTION =
    (((user.pricing_tier && user.pricing_tier !== "unknown") ||
      location.state.firstVisit ||
      location.state.onboardingComplete) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
    hasPriceOptimizationModule ||
    checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  if (!DYNAMIC_PRICING_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" />;
  }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/pricing/management/")({
  component: PricingManagementOverview,
});
